// TRIL-420: pure gate-reason helpers extracted from PostDraft and PostReplyDraft
// so both components share the same logic and the tests guard the real function.
import { charCounter } from '../constants/post.constants';

// The floor is "unknown" whenever min === null — whether the query is still
// loading OR it failed. We gate on that (not on `loading`), so a background
// revalidation that already has a cached floor never re-blocks publish. When the
// floor is unknown, `constraintsError` picks the message: an honest error (+ the
// component renders a retry) vs. the transient "checking".
// TRIL-507: an article satisfies the accessibility H1 requirement iff it has a Heading block with
// level === 1 and non-empty text. Null/undefined level does NOT count (mirrors server assertHasH1).
export function hasLevelOneHeading(
  blocks:
    | Array<{
        type?: string;
        data?: { level?: number | null; header?: string | null };
      }>
    | undefined,
): boolean {
  return (
    Array.isArray(blocks) &&
    blocks.some(
      (b) =>
        b?.type === 'Heading' &&
        b?.data?.level === 1 &&
        typeof b?.data?.header === 'string' &&
        b.data.header.trim() !== '',
    )
  );
}

export function postPublishDisabledReason(
  isAnalyzing: boolean,
  constraintsError: boolean,
  min: number | null,
  storyLength: number,
  categoriesExist: boolean,
  reAnalyzeStory: boolean,
  hasStoryAnalysis: boolean,
  // TRIL-507: accessibility — the article must have a top-level heading (H1). Server enforces
  // this too (assertHasH1); this gate just disables Publish with a clear reason.
  hasH1: boolean,
): string | null {
  const belowMinLength = min !== null && storyLength < min;
  return isAnalyzing
    ? null
    : min === null
      ? constraintsError
        ? charCounter.loadError
        : charCounter.checking
      : belowMinLength
        ? `Posts must be at least ${min} characters to publish (currently ${storyLength}).`
        : !hasH1
          ? 'Add a top-level heading (H1) to publish — required for accessibility.'
          : !categoriesExist
            ? 'Add at least one category to publish.'
            : reAnalyzeStory
              ? 'Re-analyze your edited story to publish.'
              : !hasStoryAnalysis
                ? 'Analyze your story to publish.'
                : null;
}

export function replyPublishDisabledReason(
  isAnalyzing: boolean,
  constraintsError: boolean,
  min: number | null,
  replyLength: number,
  hasStoryAnalysis: boolean,
): string | null {
  const belowMinLength = min !== null && replyLength < min;
  return isAnalyzing
    ? null
    : min === null
      ? constraintsError
        ? charCounter.loadError
        : charCounter.checking
      : belowMinLength
        ? `Replies must be at least ${min} characters to publish (currently ${replyLength}).`
        : !hasStoryAnalysis
          ? 'Analyze your reply to publish.'
          : null;
}
